<script lang="ts" setup>
import type { HTMLAttributes } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { cn } from '@@/packages/app/libs/css';
import type { MaterialSymbolName } from '@shared/types/materialSymbolNames';
import type { IconColor, IconSize } from './BaseIcon.vue';

export type ButtonVariant = 'filled' | 'outline-strong' | 'outline' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large';

const props = withDefaults(
  defineProps<{
    type: 'submit' | 'button';
    size?: ButtonSize;
    variant?: ButtonVariant;
    destructive?: boolean;
    active?: boolean;
    icon?: MaterialSymbolName | 'spinner';
    iconPosition?: 'left' | 'right';
    alt?: string;
    dropdown?: boolean;
    loading?: boolean;
    // Do NOT disable submit buttons when forms are invalid - use validation
    // instead. Some users click submit to find errors and work out what to
    // complete next
    disabled?: boolean;
    route?: RouteLocationRaw;
    href?: string;
    class?: HTMLAttributes['class'];
  }>(),
  {
    size: 'medium',
    variant: 'outline',
    iconPosition: 'left',
  },
);

defineEmits<{
  click: [e: MouseEvent];
}>();

const componentType = computed(() => {
  if (props.route && !props.disabled) return 'router-link';
  if (props.href && !props.disabled) return 'a';
  return 'button';
});

const routeAttrs = computed(() => {
  if (props.route) {
    return {
      to: props.route,
    };
  }
  if (props.href) {
    return {
      href: props.href,
      target: '_blank',
      rel: 'noopener noreferred',
    };
  }
  return {};
});

const iconColor = computed<IconColor>(() => {
  if (props.disabled) return 'disabled';
  if (props.variant === 'filled') return 'on-fill';
  if (props.destructive) return 'error';
  return 'neutral';
});

const isActiveProvided = inject<ComputedRef<boolean> | undefined>('buttonActive', undefined);
const isActive = computed(() => isActiveProvided?.value || props.active);

// The button's size scale (small/medium/large) maps onto the icon's sm/md/lg.
// Spinner and the button's own CSS keep using `size` directly.
const iconSize = computed<IconSize>(() => {
  if (props.size === 'small') return 'sm';
  if (props.size === 'large') return 'lg';
  return 'md';
});
</script>

<template>
  <component
    :is="componentType"
    v-bind="{ ...routeAttrs, ...$attrs }"
    :class="
      cn(
        'bb-button inline-block font-semibold no-underline',
        size,
        `bb-${variant}`,
        {
          destructive: !disabled && destructive,
          active: isActive && !disabled,
          disabled,
          dropdown,
        },
        props.class,
      )
    "
    :disabled="disabled || loading"
    :type="route && !disabled ? null : type"
    @click="$emit('click', $event)"
  >
    <!-- Leave only: BaseSpinner fades itself in -->
    <Transition name="spinner">
      <div v-if="loading" class="spinner">
        <BaseSpinner :size="size" :color="variant === 'filled' ? 'on-fill' : 'neutral'" />
      </div>
    </Transition>
    <span
      v-if="icon && iconPosition === 'left'"
      class="content"
      :class="{ loading, 'icon-lead': $slots.default }"
      :aria-hidden="loading || undefined"
    >
      <BaseSpinner
        v-if="icon === 'spinner'"
        :size="size === 'small' ? 'small' : 'large'"
        :color="variant === 'filled' ? 'on-fill' : 'neutral'"
        class="align-middle"
      />
      <BaseIcon v-else :size="iconSize" :name="icon" :color="iconColor" />
    </span>
    <span class="bb-label content" :class="{ loading }" :aria-hidden="loading || undefined">
      <slot />
    </span>
    <span v-if="alt" class="sr-only">{{ alt }}</span>
    <span
      v-if="(icon && iconPosition === 'right') || href"
      class="content"
      :class="{ loading, 'icon-trail': $slots.default }"
      :aria-hidden="loading || undefined"
    >
      <BaseSpinner
        v-if="icon === 'spinner' && !href"
        :size="size === 'small' ? 'small' : 'large'"
        :color="variant === 'filled' ? 'on-fill' : 'neutral'"
        class="align-middle"
      />
      <BaseIcon
        v-else
        :size="iconSize"
        :name="href || !icon || icon === 'spinner' ? 'open_in_new' : icon"
        :color="disabled ? undefined : iconColor"
      />
    </span>
    <span
      v-if="dropdown"
      class="dropdown content"
      :class="{ loading }"
      :aria-hidden="loading || undefined"
    >
      <BaseIcon :size="iconSize" name="keyboard_arrow_down" :color="iconColor" />
    </span>
  </component>
</template>

<style scoped>
.bb-button {
  position: relative;
  box-sizing: border-box;
  font-family: inherit;
  line-height: var(--line-height-base);
  white-space: nowrap;
  cursor: pointer;
  border-style: solid;
  border-width: 1px;
  border-radius: var(--radius-sm);
  background-color: var(--background-color-default);
  color: var(--text-color-neutral);
  text-align: center;
  transition: all var(--transition-duration-fast) var(--ease-rl-out);
  box-shadow: var(--shadow-none);

  &:hover:not(.disabled) {
    box-shadow: var(--shadow-sm);
  }

  &.disabled {
    cursor: not-allowed;
  }

  /* icon 8px from the edge, text 12px */
  .bb-label:not(:empty) {
    padding-inline: var(--spacing-1x);
  }

  .icon-lead {
    margin-right: var(--spacing-0\.5x);
  }

  .icon-trail {
    margin-left: var(--spacing-0\.5x);
  }

  &.large {
    .icon-lead {
      margin-right: var(--spacing-1x);
    }

    .icon-trail {
      margin-left: var(--spacing-1x);
    }
  }

  /* Offset so the ring contrasts with the page background rather than the button fill */
  &:focus-visible {
    outline: 2px solid var(--border-color-focus);
    outline-offset: 2px;
  }

  &.small {
    padding: var(--spacing-1\.5x) var(--spacing-2x);
    font-size: var(--text-sm);
    line-height: var(--text-sm--line-height);

    &.icon-button {
      line-height: 1;
      padding: var(--spacing-1\.5x);
    }
  }

  &.medium {
    padding: var(--spacing-1\.5x) var(--spacing-2x);
    font-size: var(--text-base);

    &.icon-button {
      padding: var(--spacing-1\.5x) calc(var(--spacing-1\.5x) + 1px);
    }

    &.dropdown {
      padding-right: var(--spacing-2x);
    }
  }

  &.large {
    padding: var(--spacing-2x) var(--spacing-4x);
    font-size: var(--text-lg);

    &.icon-button {
      padding: var(--spacing-2x);
    }
  }

  &.bb-filled {
    color: var(--text-color-on-fill);
    background-color: var(--background-color-strong-primary);
    border-color: var(--background-color-strong-primary);

    &:hover,
    &:focus-visible,
    &.active {
      background-color: var(--background-color-strong-primary-hover);
    }

    &.disabled {
      background-color: var(--background-color-neutral-disabled);
    }

    &.destructive {
      background-color: var(--background-color-strong-error);
      border-color: var(--background-color-strong-error);

      &:hover,
      &:focus-visible,
      &.active {
        background-color: var(--background-color-strong-error-hover);
      }
    }
  }

  &.bb-outline-strong {
    border-color: var(--border-color-strong-neutral-muted);

    &:hover,
    &:focus-visible {
      background-color: var(--background-color-hover);
    }

    &.destructive {
      border-color: var(--border-color-strong-error);

      &:hover,
      &:focus-visible {
        background-color: var(--background-color-error-hover);
      }
    }
  }

  &.bb-outline {
    border-color: var(--border-color-neutral);

    &:hover,
    &:focus-visible,
    &.active {
      background-color: var(--background-color-hover);
    }

    &.destructive {
      border-color: var(--border-color-error);

      &:hover,
      &:focus-visible,
      &.active {
        background-color: var(--background-color-error-hover);
      }
    }
  }

  /* we need a bit of extra specificity to override destructive buttons */
  &.disabled.disabled:not(.bb-text) {
    color: var(--text-color-neutral-disabled);
    border-color: var(--border-color-neutral-disabled);
  }

  &.bb-text {
    border-color: transparent;
    background-color: transparent;

    /* hover and focus keep the edge so the shadow reads, but no fill */
    &:hover,
    &:focus-visible {
      border-color: var(--border-color-neutral);

      &.destructive {
        border-color: var(--border-color-error);
      }
    }

    &.active {
      background-color: var(--background-color-hover);
      border-color: var(--border-color-neutral);

      &.destructive {
        border-color: var(--border-color-error);
      }
    }

    &.disabled {
      color: var(--text-color-neutral-disabled);
    }
  }

  &:not(.bb-filled).destructive {
    color: var(--text-color-error);
  }

  .dropdown {
    display: inline-block;
    opacity: 0.6;
    margin-left: var(--spacing-1x);
  }
}

.spinner {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.spinner-leave-active {
  transition: opacity var(--transition-duration-fast) var(--ease-rl-out);
}

.spinner-leave-to {
  opacity: 0;
}

/* Crossfades with the spinner; aria-hidden drops the label's name straight away */
.content {
  transition: opacity var(--transition-duration-base) var(--ease-rl-out);

  &.loading {
    visibility: hidden;
    opacity: 0;
    transition:
      opacity var(--transition-duration-fast) var(--ease-rl-out),
      visibility 0s var(--transition-duration-fast);
  }
}
</style>
